import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, type User } from "../api";
import { useAuth } from "../auth";
import { Button, Card, Input, Ornament } from "../components/ui";

type Mode = "password" | "magic";

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage() {
  const { user, refresh } = useAuth();
  const location = useLocation();

  // If we're already signed in (login just succeeded, or the user landed
  // here while authed), bounce to wherever they were headed — or home.
  if (user) {
    const dest = (location.state as LocationState | null)?.from?.pathname ?? "/";
    return <Navigate to={dest} replace />;
  }
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<Mode>("password");
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signInWithPassword(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.post<{ user: User }>("/auth/login", { email, password });
      await refresh();
      // AuthProvider will route us to the app once `user` is non-null.
    } catch (err) {
      setError((err as Error).message || "Sign in failed.");
    } finally {
      setLoading(false);
    }
  }

  async function sendMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<{ sent: boolean; devLink: string | null }>("/auth/request", { email });
      setSent(res.sent);
      setDevLink(res.devLink);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12 text-bordeaux">
          <div className="font-mono text-[10px] tracking-[0.30em] uppercase text-ink-mute mb-3">
            A · Reading · Society
          </div>
          <h1 className="font-serif italic text-6xl tracking-tight leading-[0.95] mb-3">
            Hamilton Book Club
          </h1>
          <div className="font-mono text-[10px] tracking-[0.30em] uppercase text-ink-mute">
            Circa 2025
          </div>
        </div>

        <Card accent>
          {sent ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-center w-12 h-12 mx-auto rounded-full bg-blush-soft text-rose-dark">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 8l9 6 9-6M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="font-serif text-2xl text-center">Check your inbox</h2>
              <p className="text-sm text-plum-soft text-center leading-relaxed">
                We sent a sign-in link to <span className="font-medium text-plum">{email}</span>.
                <br />
                It will expire in 30 minutes.
              </p>
              {devLink ? (
                <div className="mt-2 p-4 rounded-xl bg-cream-100 border border-cream-300">
                  <div className="text-[0.7rem] uppercase tracking-[0.16em] text-gold-dark mb-2">
                    Dev mode link
                  </div>
                  <a href={devLink} className="text-sm text-rose-dark underline break-all">
                    {devLink}
                  </a>
                </div>
              ) : null}
              <button
                onClick={() => {
                  setSent(false);
                  setMode("password");
                }}
                className="text-xs uppercase tracking-[0.16em] text-plum-soft hover:text-rose mt-2"
              >
                Use a different email
              </button>
            </div>
          ) : mode === "password" ? (
            <form onSubmit={signInWithPassword} className="flex flex-col gap-5">
              <div>
                <h2 className="font-serif text-2xl mb-1.5">Welcome back</h2>
                <p className="text-sm text-plum-soft leading-relaxed">
                  Sign in with your password — or use a magic link if you haven't set one yet.
                </p>
              </div>
              <Input
                label="Email"
                type="email"
                required
                autoFocus
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Password"
                type="password"
                required
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={error}
              />
              <Button type="submit" loading={loading}>
                Sign in
              </Button>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setMode("magic");
                }}
                className="text-xs uppercase tracking-[0.16em] text-plum-soft hover:text-rose"
              >
                First time? Send me a magic link instead
              </button>
            </form>
          ) : (
            <form onSubmit={sendMagicLink} className="flex flex-col gap-5">
              <div>
                <h2 className="font-serif text-2xl mb-1.5">Sign in by email</h2>
                <p className="text-sm text-plum-soft leading-relaxed">
                  We'll send you a one-time sign-in link. Once you're in, you can set a password
                  from your profile so you won't need this next time.
                </p>
              </div>
              <Input
                label="Email"
                type="email"
                required
                autoFocus
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={error}
              />
              <Button type="submit" loading={loading}>
                Send my sign-in link
              </Button>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setMode("password");
                }}
                className="text-xs uppercase tracking-[0.16em] text-plum-soft hover:text-rose"
              >
                I have a password
              </button>
            </form>
          )}
        </Card>

        <div className="mt-12">
          <Ornament />
        </div>
      </div>
    </div>
  );
}
